/**
 * Pure, UI-side helpers for the SharePoint import feature.
 *
 * These functions carry NO React/Redux state and NO network I/O — they express
 * the selection model (which SharePoint folders the admin has ticked) and the
 * folder-path display semantics that mirror the backend's folder-path model
 * (`functions/shared/sharepoint/folder-path.ts`).
 *
 * A selected folder is identified by a **stable key** `${driveId}:${itemId}`
 * so the same folder can never appear twice in a selection regardless of how it
 * was reached during the drill-down.
 *
 * Feature: sharepoint-import
 * Requirements: 4.1, 7.4
 */

/** A SharePoint folder chosen as an import root. Mirrors the POST payload element. */
export interface SelectedFolder {
  driveId: string;
  itemId: string;
  /** The display path segments of the folder (site/library/…/folder). */
  path: string[];
}

/**
 * The stable identity of a folder within the selection model. Two folders are
 * the same selection iff they share a `driveId` and `itemId`.
 */
export function selectionKey(folder: { driveId: string; itemId: string }): string {
  return `${folder.driveId}:${folder.itemId}`;
}

/**
 * Display string for a folder path — joins the segments with '/'. Mirrors the
 * backend `toFolderPathStr` (`folderPath.join('/')`) so what the admin sees in
 * the UI matches where the media will land.
 */
export function folderPathToString(path: string[]): string {
  return path.join('/');
}

/** True iff a folder (by key) is already in the selection. */
export function isSelected(
  selection: SelectedFolder[],
  folder: { driveId: string; itemId: string },
): boolean {
  const key = selectionKey(folder);
  return selection.some((s) => selectionKey(s) === key);
}

/**
 * Adds a folder to the selection. Idempotent: adding a folder already present
 * (same key) returns the selection unchanged (no duplicate). The `path` of the
 * existing entry is preserved.
 */
export function addSelection(selection: SelectedFolder[], folder: SelectedFolder): SelectedFolder[] {
  if (isSelected(selection, folder)) {
    return selection;
  }
  return [...selection, folder];
}

/**
 * Removes a folder from the selection by key. Removing a folder that is not
 * present is a no-op (returns an equivalent selection).
 */
export function removeSelection(
  selection: SelectedFolder[],
  folder: { driveId: string; itemId: string },
): SelectedFolder[] {
  const key = selectionKey(folder);
  return selection.filter((s) => selectionKey(s) !== key);
}

/**
 * Toggles a folder's membership in the selection: adds it if absent, removes it
 * if present.
 */
export function toggleSelection(selection: SelectedFolder[], folder: SelectedFolder): SelectedFolder[] {
  return isSelected(selection, folder)
    ? removeSelection(selection, folder)
    : addSelection(selection, folder);
}

/**
 * Builds the `POST /sharepoint/imports` request payload from the current
 * selection. Strips any UI-only fields down to the `{ driveId, itemId, path }`
 * shape the import-api Lambda expects.
 */
export function buildImportPayload(selection: SelectedFolder[]): {
  selectedFolders: SelectedFolder[];
} {
  return {
    selectedFolders: selection.map((s) => ({
      driveId: s.driveId,
      itemId: s.itemId,
      path: [...s.path],
    })),
  };
}
